/**
 * F-112 (TASK-0366, REQ-045-001, REQ-291-001): client dei documenti OpenAPI (solo Admin, il server risponde 403 agli altri).
 * I documenti sono due: `interne` (le API dell'applicazione) ed `esterne` (solo `/api/v1/*`, con chiave).
 */
export type ApiDocumentName = 'interne' | 'esterne'

export interface ApiOperation {
  method: string
  path: string
  summary: string | null
  /** Modulo (tag dell'operazione). */
  tag: string
  /** Policy di autorizzazione dichiarate sull'operazione (`anonimo`, `autenticato`, nome della policy, `ruolo:X`). */
  policies: string[]
}

interface OpenApiOperationJson {
  summary?: string
  tags?: string[]
  'x-authorization-policies'?: string[]
}

interface OpenApiDocument {
  info?: { title?: string; version?: string; description?: string }
  paths?: Record<string, Record<string, OpenApiOperationJson | undefined>>
}

export interface ApiDocument {
  title: string
  version: string
  description: string
  operations: ApiOperation[]
}

const METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options']

const url = (name: ApiDocumentName) => `/api/platform/openapi/${name}.json`

async function fetchRaw(name: ApiDocumentName): Promise<string> {
  const res = await fetch(url(name), { credentials: 'include' })
  if (!res.ok) throw new Error(res.status === 403 ? 'Accesso non autorizzato' : `Documento non disponibile (${res.status})`)
  return res.text()
}

/** Legge un documento OpenAPI e lo riduce a elenco di operazioni (metodo, percorso, modulo, policy). */
export function parseApiDocument(text: string): ApiDocument {
  const doc = JSON.parse(text) as OpenApiDocument
  const operations: ApiOperation[] = []
  for (const [path, item] of Object.entries(doc.paths ?? {})) {
    for (const method of METHODS) {
      const op = item[method]
      if (!op) continue
      operations.push({
        method: method.toUpperCase(),
        path,
        summary: op.summary ?? null,
        tag: op.tags?.[0] ?? 'Altro',
        policies: op['x-authorization-policies'] ?? [],
      })
    }
  }
  operations.sort((a, b) => a.tag.localeCompare(b.tag) || a.path.localeCompare(b.path) || a.method.localeCompare(b.method))
  return {
    title: doc.info?.title ?? '',
    version: doc.info?.version ?? '',
    description: doc.info?.description ?? '',
    operations,
  }
}

export const apiDocsApi = {
  get: async (name: ApiDocumentName): Promise<ApiDocument> => parseApiDocument(await fetchRaw(name)),
  /** Scarica il JSON del documento così com'è. */
  download: async (name: ApiDocumentName): Promise<void> => {
    const text = await fetchRaw(name)
    const blob = new Blob([text], { type: 'application/json' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `equal-crm-api-${name}.json`
    link.click()
    URL.revokeObjectURL(link.href)
  },
}
